import { AbstractOutdoorStoreFormModel } from './OutdoorLimitationCampaignFormModel';
import config from 'config';
import { RtbCampaignPlanType } from 'core/rtbCampaign/RtbCampaign';
import { L1ObjectObjective } from 'core/l1Object/L1Object';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import {
  CampaignAdTypeSetting,
  ChannelGroupSetting
} from 'core/channelGroup/ChannelGroupSettings';
import { RtbCampaignBasicFormModelConstructorParams } from './RtbCampaignBasicFormModel';
import {
  AdRequestSourceManager,
  DefaultAdRequestSourceManager
} from 'core/adRequestSource/AdRequestSourceManager';
import { compact } from 'lodash';
import { ADDONFEATURE, perChannelCampaignKey } from 'core/agency/AddonFeature';
import { PIC_RELATED_CHANNELS } from 'core/channelGroup/ChannelGroup';

const adRequestSourceManager: AdRequestSourceManager =
  new DefaultAdRequestSourceManager();

export abstract class DynamicOutdoorStoreFormModel extends AbstractOutdoorStoreFormModel {
  adTypeSetting: CampaignAdTypeSetting | undefined;

  get campaignAdType() {
    return this.adTypeSetting?.name || 'DYNAMIC_STORE_OUTDOOR';
  }

  override get mapsApiKey(): string {
    return PIC_RELATED_CHANNELS.includes(this.channelGroupSetting.config.id)
      ? config.picGoogleMapsApiKey
      : config.googleMapsApiKey;
  }

  override getRtbOptionsMap() {
    const pricingModelOptimizeModel = {
      [RtbCampaignPlanType.FCPM]: L2ObjectOptimizationGoal.IMPRESSIONS,
      [RtbCampaignPlanType.FACPM]: L2ObjectOptimizationGoal.ACPM,
      [RtbCampaignPlanType.FWCPM]: L2ObjectOptimizationGoal.WCPM
    };
    const optionsMap = {
      [L1ObjectObjective.AWARENESS]: {}
    };
    const addonMap = {
      [RtbCampaignPlanType.RS]: ADDONFEATURE.CAMPAIGN.REVENUE_SHARING,
      [RtbCampaignPlanType.FCPM]: ADDONFEATURE.CAMPAIGN.FIXED_CPM,
      [RtbCampaignPlanType.FACPM]: ADDONFEATURE.CAMPAIGN.FIXED_ACPM,
      [RtbCampaignPlanType.FWCPM]: ADDONFEATURE.CAMPAIGN.FIXED_WCPM
    };
    const pricingModels = (this.adTypeSetting?.pricingModel || []).filter(
      (model) =>
        addonMap[model]
          ? this.addonFeatureManager.isFeatureEnable(
              perChannelCampaignKey(
                this.l1Object.channelGroupId,
                addonMap[model]
              )
            )
          : false
    );
    const optimizeModels = compact(
      pricingModels.map((model) => pricingModelOptimizeModel[model])
    );
    pricingModels.forEach((pricingModel) => {
      if (pricingModel === RtbCampaignPlanType.RS || this.optimizeAddonEnable) {
        optionsMap[L1ObjectObjective.AWARENESS][pricingModel] = optimizeModels;
      } else {
        const optimizeModel =
          pricingModelOptimizeModel[pricingModel as RtbCampaignPlanType];
        if (optimizeModel) {
          optionsMap[L1ObjectObjective.AWARENESS][pricingModel] = [
            optimizeModel
          ];
        }
      }
    });
    return optionsMap;
  }

  override async getSpaceGroups(): Promise<SelectOptions[]> {
    return adRequestSourceManager.getSpacesGroupsOfAdType(
      this.campaignAdType,
      this.adTypeSetting!.adTypeId
    );
  }
}

export class CreateDynamicOutdoorStoreFormModel extends DynamicOutdoorStoreFormModel {
  constructor(
    channelGroupSetting: ChannelGroupSetting,
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super(channelGroupSetting, 'create', ...args);
  }
  get canEditBudgetPlan(): boolean {
    return true;
  }
  get canEditPriceModel(): boolean {
    return true;
  }
}

export class EditDynamicOutdoorStoreFormModel extends DynamicOutdoorStoreFormModel {
  constructor(
    channelGroupSetting: ChannelGroupSetting,
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super(channelGroupSetting, 'edit', ...args);
  }
  get canEditBudgetPlan(): boolean {
    return false;
  }
  get canEditPriceModel(): boolean {
    return false;
  }
  canEditOptimize(): boolean {
    return false;
  }
}
